import { YYYYMMDD } from "@/helpers/dateHelper";
import { GetPricedDeparturesRequest } from "@/models/api/GetPricedDeparturesRequest";
import { PriceDepartureRequest } from "@/models/api/PriceDepartureRequest";
import { WPContent } from "@/models/Content";
import { DirectionEnum, PassengerTypeEnum, TicketTypeEnum } from "@/models/enums";
import { DeparturePrice, DepartureTicketType } from "@/models/www/DeparturePrice";
import contentService from "@/services/content.service";
import departureService from "@/services/departure.service";
import { useBookingStore } from "@/store/booking";
import { useUserStore } from "@/store/user";
import { computed, ref } from "vue";
import useAuth from "./useAuth";
import useDayTrip from "./useDayTrip";

const outwardDepartures = ref<DeparturePrice[]>([]);
const returnDepartures = ref<DeparturePrice[]>([]);

const useDepartures = () => {
	const { reAuthenticate } = useAuth();
	const { getIsLoggedIn } = useUserStore();
	const { initializeRedirect } = useAuth();
	const bookingStore = useBookingStore();
	const modeOfTravel = computed(() => { return bookingStore.booking.modeOfTravel });
	const { isDayTrip } = useDayTrip();
	//checking the selected outward arrival date
	//so we can disable return departures
	//that is before the arrival date
	const outwardArrivalDate = computed(() => {
		const departureId = bookingStore.getDeparture(DirectionEnum.Outward).id;
		const arrivalDate = outwardDepartures.value.find(d => d.departureId === departureId)?.arrivalDate ?? new Date();
		return arrivalDate;
	});

	const returnDepartureDate = computed(() => {
		const departureId = bookingStore.getDeparture(DirectionEnum.Return).id;
		const departureDate = returnDepartures.value.find(d => d.departureId === departureId)?.arrivalDate ?? new Date();
		return departureDate;
	});

	const fetchDepartures = (): Promise<void> => {
		const handleResponse = (result: DeparturePrice[]) => {
			outwardDepartures.value = getDeparturesByRoute(DirectionEnum.Outward, result);

			//disable departures that shouldnt be selectable
			const departures = getDeparturesByRoute(DirectionEnum.Return, result);
			returnDepartures.value = getAvailableDepartures(departures);
		}

		const fn = () => {
			return getDepartures(DirectionEnum.Outward);
		}

		return new Promise((resolve, reject) => {
			if (!getIsLoggedIn) {
				reAuthenticate<DeparturePrice[]>(fn).then((resp) => {
					handleResponse(resp);
					resolve();
				}).catch((err) => {
					reject(err)
				});
			}
			else {
				fn().then((resp) => {
					handleResponse(resp);
					resolve();
				}).catch(err => {
					reject(err)
				});
			}
		})
	}

	const getDeparturesByRoute = (direction: DirectionEnum, departures: DeparturePrice[]) => {
		const route = bookingStore.getRoute(direction);
		return departures.filter(x => x.route === route);
	}

	const updateDepartureState = (departures: DeparturePrice[], direction: DirectionEnum) => {

		let identifier = departures.flatMap(x => x.ticketTypes)
			.find((tg) => tg.id === bookingStore.getDeparture(direction).ticketType.id)?.identifier ?? "";

		// for amendments we need to fetch 
		// the identifier from an departure
		// since we only have the date and route for booked departure
		if (!identifier) {
			const departureState = bookingStore.getDeparture(direction);
			const departure = departures.find(d => d.departureId === departureState.id);

			identifier = departure?.ticketTypes.find(t => t.type === TicketTypeEnum[departureState.type])?.identifier ?? "";
		}


		bookingStore.setDeparture(direction, {
			...bookingStore.getDeparture(direction),
			identifier
		});

		bookingStore.setDepartureTicketType(direction, {
			...bookingStore.getDeparture(direction).ticketType,
			identifier
		});
	}

	const getDepartures = (direction: DirectionEnum): Promise<DeparturePrice[]> => {
		
		const departures = [] as PriceDepartureRequest[];
		const trips = bookingStore.getBooking.tripType === DirectionEnum.Return ? [DirectionEnum.Outward, DirectionEnum.Return] : [DirectionEnum.Outward];

		trips.forEach((d) => {
			
			departures.push({
				route: bookingStore.getRoute(d),
				date: YYYYMMDD(bookingStore.getDate(d) ?? new Date()),
				returnDate: YYYYMMDD(bookingStore.getDate(d) ?? new Date()),
				passengers: bookingStore.getPassengers(DirectionEnum.Outward).filter(p => p.type !== PassengerTypeEnum.Dog).map(x => x.type),
				numberOfPets: bookingStore.getPassengers(DirectionEnum.Outward).filter(p => [PassengerTypeEnum.Dog].includes(p.type)).length
			});
		});

		if (!departures.every(d => d.route !== '')) {
			initializeRedirect(true);
			return Promise.resolve([]);
		}

		const pricedDepartureReq = {
			travelType: bookingStore.getBooking.modeOfTravel,
			requestedRoute: bookingStore.getRoute(direction),
			departures: departures
		} as GetPricedDeparturesRequest;

		//fetch content from cms and departures from server
		const promises: Promise<unknown>[] = [
			departureService.searchDepartures(pricedDepartureReq),
			contentService.getContent("departures", modeOfTravel.value,
				bookingStore.getRoute(DirectionEnum.Outward))
		];

		return new Promise((resolve, reject) => {
			Promise.all(promises).then(resp => {
				//check departurestate if customer 
				//went back and change passenger amount
				//if true we need to update the identifier in departurestate
				[DirectionEnum.Outward, DirectionEnum.Return]
					.map((d) => {
						updateDepartureState(resp[0] as DeparturePrice[] ?? [], d);
					});

				resolve((resp[0] as DeparturePrice[]).map(d => syncContent(d, resp[1] as WPContent[])));
			}).catch(err => reject(err));
		})
	}

	const syncContent = (departure: DeparturePrice, content: WPContent[]) => {
		const ticketContent = [] as DepartureTicketType[];

		//Remove daytrip ticket alternatives if not a daytrip
		if (!isDayTrip.value) {
			departure.ticketTypes = departure.ticketTypes.filter(t => t.type !== "Daytrip")
		}

		departure.ticketTypes.forEach(d => {
			//since there is a different enum in cms but the same letters in beginning, 
			//we use startswith match here
			d.body = content.find(c => c.id.startsWith(d.type))?.body ?? "";
			d.title = content.find(c => c.id.startsWith(d.type))?.title ?? "";

			ticketContent.push(d);
		});

		departure.ticketTypes = ticketContent;

		return departure;
	}

	const flushReturnDeparture = () => {
		if (returnDepartureDate.value) {

			//clear departure if outward arrival date
			//is after the return departure date
			if (outwardArrivalDate.value > returnDepartureDate.value) {
				bookingStore.clearDeparture(DirectionEnum.Return);
			}
		}
	}

	const getAvailableDepartures = (departures: DeparturePrice[]) => {
		const departureList = [] as DeparturePrice[];
		departures.forEach((departure) => {
			if (departure.departureDate < (outwardArrivalDate.value ?? new Date())) {
				//set the departure to disabled
				//because it is before the outward
				//arrival date
				departure.disabled = false;
			}
			else {
				departure.disabled = false;
			}

			departureList.push(departure);
		});

		return departureList;
	}

	return {
		getDepartures,
		getAvailableDepartures,
		flushReturnDeparture,
		getDeparturesByRoute,
		fetchDepartures,
		outwardArrivalDate,
		outwardDepartures,
		returnDepartures,
		returnDepartureDate
	}
}

export default useDepartures;